import { useEffect, useMemo, useState } from "react"
import { useQuery } from "urql"

import { GetBaseWarehouseListDocument, type WarehouseAreaOutputList } from "@/graphql/codegen/graphql"
import type { Option } from "@/types/form-fields"
import { getWarehouseOptions } from "@/utils/warehouse"

interface UseGetWarehouseOptionsParams {
  isActive?: boolean
  isInventoryRelated?: boolean
  productId?: number
}

interface UseGetWarehouseOptionsReturn {
  warehouseOptions: Option<string>[]
  isFetching: boolean
}

const LIMIT = 100

export const useGetWarehouseOptions = ({
  isActive,
  isInventoryRelated,
  productId,
}: UseGetWarehouseOptionsParams = {}): UseGetWarehouseOptionsReturn => {
  const [offset, setOffset] = useState(0)
  const [warehouseAreas, setWarehouseAreas] = useState<WarehouseAreaOutputList[]>([])

  const [{ data, fetching }] = useQuery({
    query: GetBaseWarehouseListDocument,
    variables: {
      input: {
        filter: { isActive, isInventoryRelated, productId },
        offset,
        limit: LIMIT,
      },
    },
  })

  const output = data?.warehouseArea.list

  useEffect(() => {
    const paginatedWarehouseAreas =
      output?.__typename === "WarehouseAreaListSuccess" ? (output.warehouseAreas as WarehouseAreaOutputList[]) : []
    setWarehouseAreas((prev) => (offset === 0 ? paginatedWarehouseAreas : [...prev, ...paginatedWarehouseAreas]))

    if (paginatedWarehouseAreas.length === LIMIT) {
      setOffset((prevOffset) => prevOffset + LIMIT)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const warehouseOptions = useMemo(() => getWarehouseOptions(warehouseAreas), [warehouseAreas])

  return { warehouseOptions, isFetching: fetching }
}
